import { useState } from "react";
import { useQuery } from "@tanstack/react-query";
import { queryClient } from "@/queryClient";
import { toast } from "react-toastify";
import { addTrackComment, getMentionList } from "../../api";
import { queryCommonFlags } from "@/api/constants";
import { TrackCommentSource } from "../../dtos";
import { useDomainMutation } from "@/modules/common/hooks/domain-mutation.hook";

const useAddComments = ({
  trackUId,
  source,
}: {
  trackUId: string;
  source?: TrackCommentSource;
}) => {
  const [formatedComment, setFormatedComment] = useState<string>("");
  const [commentMessage, setCommentMessage] = useState<string>("");
  const [mentionIds, setMentionIds] = useState<string>("");
  const [mentionedUsers, setMentionedUsers] = useState<any[]>([]);

  const [dialogOpenFeedback, setDialogOpenFeedback] = useState(false);
  const [addCommentmobile, setAddCommentMobile] = useState<boolean>(false);
  const [notifySalesAgent, setNotifySalesAgent] = useState<boolean>(false);

  const { data: mention_list } = useQuery({
    queryKey: ["mentionable-users", trackUId],
    queryFn: () => getMentionList(trackUId),
    ...queryCommonFlags,
    enabled: !!trackUId,
  });

  const addCommentMutation = useDomainMutation(
    (data: any) =>
      addTrackComment({
        trackUId,
        data: {
          ...data,
          mentions: data.mentions?.length ? data.mentions : undefined,
          source,
        },
      }),
    {
      onSuccess: () => {
        setNotifySalesAgent(false);
        queryClient.invalidateQueries({ queryKey: ["track"] });
        setAddCommentMobile(false);
        setCommentMessage("");
        setMentionIds("");

        toast.success("Comment added successfully", {
          position: "bottom-left",
          autoClose: 3000,
        });
      },
      onError: () => {
        setNotifySalesAgent(false);
        toast.error("Failed to add comment", {
          position: "bottom-left",
          autoClose: 3000,
        });
      },
    },
  );

  const extractMentionIds = (text: string): string => {
    const regex = /@\[[^\]]{1,200}\]\(([\w-]+)\)/g;
    const ids = new Set<string>();

    let match;
    while ((match = regex.exec(text)) !== null) {
      ids.add(match[1]);
    }

    return Array.from(ids).join(",");
  };

  const handleTextareaChange = (event: { target: { value: string } }) => {
    const newMessage = event.target.value;
    setCommentMessage(newMessage);

    setFormatedComment(newMessage);

    setMentionIds(extractMentionIds(event.target.value));

    if (mention_list?.data) {
      setMentionedUsers(mention_list.data);
    }
  };

  const handleKeyPress = (event: React.KeyboardEvent<HTMLTextAreaElement>) => {
    if (
      event.key === "Enter" &&
      !event.shiftKey &&
      commentMessage &&
      !addCommentMutation.isPending
    ) {
      event.preventDefault();
      handleAddComments();
    }
  };

  const handleAddComments = async () => {
    const data: {
      comment: string;
      notifySalesAgent?: boolean;
      mentions?: string[];
    } = {
      comment: formatedComment,
      mentions: [],
    };

    if (notifySalesAgent) {
      data.notifySalesAgent = true;
    }

    if (mentionIds.length > 0) {
      const idsArray = mentionIds.split(",").map((id) => id.trim());
      data.mentions = idsArray;
    }

    addCommentMutation.mutate(data);
  };

  return {
    commentMessage,
    mentionedUsers: mentionedUsers.map((user: any) => ({
      id: user.uid,
      display: user.name,
      color: user.color,
      name: user.name,
    })),
    mentionIds,
    dialogOpenFeedback,
    errorAddingComment: addCommentMutation.isError,
    successAddingComment: addCommentMutation.isSuccess,
    loadAddingComment: addCommentMutation.isPending,
    addCommentmobile,
    notifySalesAgent,
    handleAddComments,
    handleTextareaChange,
    handleKeyPress,
    setCommentMessage,
    setMentionIds,
    setNotifySalesAgent,
    setAddCommentMobile,
    setDialogOpenFeedback,
  };
};

export default useAddComments;
